import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import _ from "lodash";
import type { FamilyMember, Category } from "@/types";
import { Modal } from "@/components/Modal/Modal";
import ListOptions from "./ListOptions";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useClosetStore, useUserStore } from "@/store";
import Logo from "../../assets/logo.png";
import styles from "./Sidebar.module.css";
import { PanelLeftOpen, PanelRightOpen } from "lucide-react";
import useOnClickOutside from "@/hooks/useOnClickOutside";

interface SidebarProps {
  members: FamilyMember[];
  categories: Category[];
  activeMemberId: string;
  activeCategoryName: string | null;
  disabled?: boolean;
  onShare: () => void;
  onLogout: () => void;
}

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 56;

export const Sidebar = memo(({ members, activeMemberId, onShare, onLogout, disabled }: SidebarProps) => {
  const sideBarRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showsLabel, setShowsLabel] = useState(false);

  const { sidebarExpanded, setSidebarExpanded } = useUserStore();

  const {
    getAllCategories,
    getAttributesFilterOptions,
    categories,
    setActiveCategory,
    setActiveAttribute,
    isLoadingCategories,
    isLoadingAttributesFilterOptions,
    filter,
    attributesFilterOptions,
    loadingInitialPage
  } = useClosetStore();

  const selectedCategories = filter?.selectedCategories;
  const selectedAttribute = filter.selectedAttributes;

  const collections = useMemo(
    () =>
      _.find(attributesFilterOptions?.product_attributes || [], (att) => att.name === "Collection")?.values.sort(
        (a, b) => (a.name < b.name ? 1 : -1)
      ) || [],
    [attributesFilterOptions]
  );

  useEffect(() => {
    getAllCategories();
    getAttributesFilterOptions();
  }, []);

  useEffect(() => {
    if (sidebarExpanded) {
      setTimeout(() => setShowsLabel(true), 150);
      return;
    }

    setShowsLabel(false);
  }, [sidebarExpanded]);

  useLayoutEffect(() => {
    setSidebarExpanded(!isMobile);
  }, [isMobile]);

  const showsSideBarOptions = (isMobile && sidebarExpanded) || !isMobile;
  const showsMobileLabels = (showsLabel && isMobile && sidebarExpanded) || !isMobile;

  useOnClickOutside(sideBarRef, () => sidebarExpanded && isMobile && setSidebarExpanded(false));

  return (
    <>
      <motion.aside
        ref={sideBarRef}
        initial={{ width: SIDEBAR_COLLAPSED_WIDTH }}
        animate={{ width: sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH }}
        className={styles.sidebar}
      >
        <div
          className={styles.logoWrap}
          style={{
            textAlign: !showsSideBarOptions ? "center" : "left",
            padding: !showsSideBarOptions ? "14px 0" : "14px 14px"
          }}
        >
          {!isMobile || sidebarExpanded ? (
            <span className={styles.logo}>Closai</span>
          ) : (
            <img src={Logo} alt="Closai Logo" className={styles.roundedLogo} />
          )}
        </div>

        <div className={styles.section}>
          <div className={[styles.label, sidebarExpanded ? styles.labelExpanded : ""].filter(Boolean).join(" ")}>
            Closets
          </div>
          {members.map((member) => (
            <button
              key={member.id}
              className={[
                styles.item,
                activeMemberId === member.id ? styles.itemActive : "",
                isMobile && sidebarExpanded ? styles.itemExpanded : ""
              ]
                .filter(Boolean)
                .join(" ")}
              // onClick={() => onMemberSelect(member.id)}
            >
              <span className={styles.avatar} style={{ background: member.color }}>
                {member.icon}
              </span>
              <span
                className={[
                  styles.itemText,
                  isMobile && !sidebarExpanded ? styles.itemTextHidden : "",
                  isMobile && sidebarExpanded ? styles.itemTextExpanded : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {member.name}
              </span>
            </button>
          ))}
        </div>

        {isMobile && (
          <div className={styles.section}>
            <button
              className={[styles.item, sidebarExpanded ? styles.itemActive : ""].filter(Boolean).join(" ")}
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
            >
              {!sidebarExpanded ? <PanelLeftOpen /> : <PanelRightOpen />}
            </button>
          </div>
        )}

        {showsSideBarOptions && !isLoadingAttributesFilterOptions && !!collections.length && (
          <ListOptions
            data={collections}
            name="Collections"
            onChange={(id) => setActiveAttribute(id, "Collections")}
            selectedValues={selectedAttribute}
            sidebarExpanded={sidebarExpanded}
          />
        )}

        <div className={styles.divider} />

        {showsSideBarOptions && !isLoadingCategories && !!categories.length && (
          <div className={styles.categoriesContainer}>
            <ListOptions
              data={categories}
              name="Departments"
              onChange={setActiveCategory}
              selectedValues={selectedCategories}
              disableValues={loadingInitialPage}
              sidebarExpanded={sidebarExpanded}
            />
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.footerBtn} disabled={disabled} onClick={onShare}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            {showsMobileLabels && (
              <span
                className={styles.footerText}
                style={{
                  display: showsSideBarOptions ? "block" : "none"
                }}
              >
                Share
              </span>
            )}
          </button>
          <button className={[styles.footerBtn, styles.logoutBtn].join(" ")} onClick={() => setShowLogoutConfirm(true)}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {showsMobileLabels && (
              <span
                className={styles.footerText}
                style={{
                  display: showsSideBarOptions ? "block" : "none"
                }}
              >
                Log out
              </span>
            )}
          </button>
          {!showsSideBarOptions && (
            <motion.a
              initial={{ marginBottom: !showsSideBarOptions ? 0 : -30, opacity: 0 }}
              animate={{ marginBottom: !showsSideBarOptions ? 0 : 0, opacity: 1 }}
              transition={{
                duration: 0.2
              }}
              href="mailto:contact@closai.io"
              className={styles.supportLink}
              style={{
                display: showsSideBarOptions ? "block" : "none"
              }}
            >
              Need help? Contact us at contact@closai.io
            </motion.a>
          )}
        </div>
      </motion.aside>

      <Modal open={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} title="Log out" maxWidth={400}>
        <p className={styles.logoutMessage}>Are you sure you want to log out?</p>
        <div className={styles.logoutActions}>
          <button className={styles.cancelBtn} onClick={() => setShowLogoutConfirm(false)}>
            Cancel
          </button>
          <button
            className={styles.confirmLogoutBtn}
            onClick={() => {
              setShowLogoutConfirm(false);
              onLogout();
            }}
          >
            Log out
          </button>
        </div>
      </Modal>
    </>
  );
});
